import FormikErrors from '@components/Formik/FormikErrors'
import FormikTextField from '@components/Formik/FormikTextField'
import { MeQuery } from '@graphql/queries/__generated__/me.generated'
import { Button } from '@mui/material'
import Grid from '@mui/material/Grid'
import { Form, Formik, FormikErrors as TFormikErrors } from 'formik'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import * as yup from 'yup'
import { ACCOUNT_CREATE_URL, ACCOUNT_LOGIN_URL, CONTACT_US_FLATPAGE_URL } from '../../../urls'
import { DOMAIN } from '../../../../constants'
import { Trans, useTranslation } from 'react-i18next'
export interface TrackOrderFormData {
  email: string
  pubId: string
}

interface TrackOrderPageProps {
  me: MeQuery['me'] | null
  onSubmit(values: TrackOrderFormData): Promise<void>
  handleSignIn(): void
  handleRegister(): void
  handleContactUs(): void
  formErrors: TFormikErrors<TrackOrderFormData>
}

const TrackOrderPage: React.FC<TrackOrderPageProps> = ({
  me,
  onSubmit,
  handleRegister,
  handleSignIn,
  handleContactUs,
  formErrors,
}) => {
  const { t } = useTranslation()
  const schema = useMemo(
    () =>
      yup.object().shape({
        pubId: yup.string().required(t('formErrors.valueIsRequired', 'This value is required')),
        email: yup
          .string()
          .email(t('formErrors.invalidEmail', 'Invalid email'))
          .required(t('formErrors.valueIsRequired', 'This value is required')),
      }),
    [t]
  )
  return (
    <Formik<TrackOrderFormData>
      initialValues={{
        pubId: '',
        email: me?.email || '',
      }}
      validationSchema={schema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <Form className='px-5 max-w-600'>
        <FormikErrors errors={formErrors} />
        <h1 className='text-xl sm:text-5xl text-center sm:text-left tracking-3/2 font-semibold uppercase mb-12'>
          {t('orderStatus', 'Order Status')}
        </h1>
        <p className='text-base mb-20'>
          {t(
            'description.checkOrderStatus',
            'Want to check the status of your order? Just enter your order number and email below.'
          )}
        </p>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <FormikTextField name='pubId' label={t('formLabels.orderNumber', 'Order Number')} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormikTextField name='email' type='email' label={t('formLabels.email', 'Email')} required />
          </Grid>
        </Grid>
        <div className='my-24'>
          <Button type='submit' variant='contained' size='large' fullWidth disableElevation>
            {t('button.trackOrder', 'Track Order')}
          </Button>
        </div>
        <div className='text-base'>
          {!me && (
            <>
              <h3 className='text-base font-bold'>{t('registeredCustomers', 'Registered Customers')}</h3>

              <p>
                <Trans
                  i18nKey='description.registerToTrackOrders'
                  defaults={`Register with {{domain}} and you can track your package for most orders. <a>Click here</a> to register`}
                  values={{ domain: DOMAIN }}
                  components={{
                    a: (
                      <Link
                        to={ACCOUNT_CREATE_URL}
                        onClick={e => {
                          e.preventDefault()
                          handleRegister()
                        }}
                      />
                    ),
                  }}
                ></Trans>
              </p>

              <p>
                {t('alreadyRegistered', 'Already registered?')}{' '}
                <Link
                  to={ACCOUNT_LOGIN_URL}
                  onClick={e => {
                    e.preventDefault()
                    handleSignIn()
                  }}
                >
                  {t('button.signIn', 'Sign In')}
                </Link>
              </p>
            </>
          )}
          <p>
            {t('stillHaveQuestions', 'Still have questions?')}{' '}
            <Link
              to={CONTACT_US_FLATPAGE_URL}
              onClick={e => {
                e.preventDefault()
                handleContactUs()
              }}
            >
              {t('button.emailUs', 'Email us')}
            </Link>
          </p>
        </div>
      </Form>
    </Formik>
  )
}

export default TrackOrderPage
